/**
 * CARTUP-1 — cart suggestion rail.
 *
 * The rail itself is Liquid (`snippets/pb-cart-suggestions.liquid`); this module only decides what a
 * pressed button does and carries it out. Three kinds, and two of them need no code of their own:
 *
 *   - `add`      a plain companion or add-on. Goes through the builder's own
 *                addItemsToCartWithDrawer, so the cart sections refresh and the drawer behaves
 *                exactly as it does everywhere else.
 *   - `navigate` a product that needs personalizing. PBPIG-1's one-builder-per-page rule sends the
 *                shopper to that product's page rather than mounting a second builder over the one
 *                this page may already have.
 *
 * A bundle companion is an ordinary suggestion here. An earlier version opened the Customizer on the
 * cart line the shopper already had and ticked the bundle, so they could complete the set at the
 * bundle price — twice that put a shopper who clicked "Monogram Bracelet" in front of a Monogram
 * Necklace, because it turns on the builder's internal state right after a cross-product mount.
 * CEO call, 2026-08-28: send them to the product they clicked. Fewer rules, nothing to get wrong.
 */

export const SUGGESTION_SELECTOR = '[data-pb-cart-suggest]';

/**
 * What a pressed suggestion button should do, read from its dataset.
 * Returns null when the button carries nothing actionable — a missing variant or url is a reason to
 * do nothing, never a reason to throw inside a click handler.
 *
 * @param {Record<string, string>} dataset
 * @returns {{ kind: 'add', variantId: number } | { kind: 'navigate', url: string } | null}
 */
export function resolveSuggestionAction(dataset) {
  if (!dataset || typeof dataset !== 'object') return null;
  const action = String(dataset.pbSuggestAction || '').trim();

  if (action === 'add') {
    const variantId = Number(String(dataset.pbSuggestVariant || '').trim());
    if (!Number.isFinite(variantId) || variantId <= 0) return null;
    return { kind: 'add', variantId };
  }

  if (action === 'navigate') {
    const url = String(dataset.pbSuggestUrl || '').trim();
    if (!url) return null;
    return { kind: 'navigate', url };
  }

  return null;
}

function setBusy(button, busy) {
  if (!button) return;
  button.classList.toggle('pb-cart-suggest__button--busy', busy);
  button.toggleAttribute('aria-busy', busy);
  button.disabled = busy;
}

/**
 * @param {{ scope?: object, doc?: object, addToCart?: (variantId: number) => Promise<any> }} [options]
 */
export function installCartSuggestions(options = {}) {
  const scope = options.scope || (typeof window !== 'undefined' ? window : null);
  if (!scope) return null;
  if (scope.__pbCartSuggestionsInstalled) return scope.__pbCartSuggestions;
  const doc = options.doc || scope.document;
  if (!doc || typeof doc.addEventListener !== 'function') return null;

  const defaultAddToCart = async (variantId) => {
    const launch = await import('@theme/pb-builder-launch');
    await launch.addItemsToCartWithDrawer([{ id: variantId, quantity: 1 }], null, '', {
      closeBuilder: false,
    });
  };
  const addToCart = options.addToCart || defaultAddToCart;


  let inFlight = null;
  const state = { lastAction: null, lastError: null };

  async function run(button) {
    const action = resolveSuggestionAction(button.dataset);
    state.lastAction = action;
    if (!action) return;

    if (action.kind === 'navigate') {
      scope.location.assign(action.url);
      return;
    }

    setBusy(button, true);
    try {
      await addToCart(action.variantId);
      state.lastError = null;
    } catch (err) {
      // addItemsToCartWithDrawer already tells the shopper; do not stack a second alert on it.
      state.lastError = err;
    } finally {
      setBusy(button, false);
    }
  }

  doc.addEventListener(
    'click',
    (event) => {
      const button = event.target?.closest?.(SUGGESTION_SELECTOR);
      if (!button || button.disabled) return;
      event.preventDefault();
      if (inFlight) return;
      inFlight = run(button).finally(() => {
        inFlight = null;
      });
    },
    true
  );

  scope.__pbCartSuggestionsInstalled = true;
  scope.__pbCartSuggestions = state;
  return state;
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  installCartSuggestions();
}
